import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DataTable,
  Button,
  useDataTableUrl,
  PageHeader,
} from '@airclou/ui'
import type { DataTableColumn } from '@airclou/ui'
import { Download } from 'lucide-react'
import { dashboardApiClient } from '../../services/api'
import type { AuditLogEntry } from '../../services/api'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeWithSeconds } from '@airclou/utils'

const PAGE_SIZE = 15

const COLUMN_SORT_MAP: Record<string, string> = {
  timestamp: 'created_at',
}

function formatAction(action: string): string {
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatResourceType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function AuditLogsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')

  const {
    search,
    page,
    sorting,
    sortOrder,
    onSortingChange,
    onSearchChange,
    onPageChange,
  } = useDataTableUrl({ columnToSortField: COLUMN_SORT_MAP, defaultLimit: PAGE_SIZE })

  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await dashboardApiClient.getAuditLogs({
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
        sortOrder: sortOrder || 'desc',
      })
      setLogs(result.data)
      setTotal(result.meta.total)
    } catch (err: any) {
      setError(err.message || 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [search, page, sortOrder])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const handleExport = useCallback(async () => {
    try {
      const blob = await dashboardApiClient.exportAuditLogs({
        search: search || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // silent
    }
  }, [search])

  const columns: DataTableColumn<AuditLogEntry>[] = useMemo(() => [
    {
      id: 'event',
      header: t('audit.event'),
      accessorKey: 'action',
      cell: (row) => (
        <div>
          <p className="font-medium text-sm text-foreground">{formatAction(row.action)}</p>
          {row.resource_name && (
            <p className="text-xs text-muted-foreground">{row.resource_name}</p>
          )}
        </div>
      ),
    },
    {
      id: 'resourceType',
      header: 'Resource',
      accessorKey: 'resource_type',
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {formatResourceType(row.resource_type)}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      id: 'actor',
      header: 'Actor',
      accessorKey: 'actor_name',
      cell: (row) => (
        <div>
          <p className="text-sm text-foreground">{row.actor_name || '-'}</p>
          {row.actor_email && (
            <p className="text-xs text-muted-foreground">{row.actor_email}</p>
          )}
        </div>
      ),
      hideOnMobile: true,
    },
    {
      id: 'ip',
      header: t('audit.ipAddress'),
      accessorKey: 'ip_address',
      cell: (row) => (
        <span className="text-sm text-muted-foreground font-mono">
          {row.ip_address || '-'}
        </span>
      ),
      hideOnMobile: true,
      hideOnTablet: true,
    },
    {
      id: 'timestamp',
      header: t('common.time'),
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTimeWithSeconds(row.created_at, timezone)}
        </span>
      ),
    },
  ], [t, timezone])

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('audit.title')}
        description={t('audit.subtitle')}
        actions={
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            {t('common.export')}
          </Button>
        }
      />

      {/* Table (search + pagination share the table's toolbar row) */}
      <DataTable
        data={logs}
        columns={columns}
        filterValue={search}
        onFilterChange={onSearchChange}
        filterPlaceholder={t('audit.searchAuditLogs')}
        sorting={sorting}
        onSortingChange={onSortingChange}
        isLoading={loading}
        error={error}
        onRetry={fetchLogs}
        emptyMessage={t('audit.noAuditLogs')}
        totalItems={total}
        currentPage={page}
        itemsPerPage={PAGE_SIZE}
        onPageChange={onPageChange}
      />
    </div>
  )
}
